import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from '../lib/icons';
import { Logo } from './ui';
import { LegalFooter, type LegalPage } from './Legal';
import './Landing.css';
import { LandingPhone } from './LandingPhone';
import { LandingMako } from './LandingMako';
import { LandingPlans } from './LandingPlans';

interface Props {
  onLogin: () => void;
  onRegister: () => void;
  onNavigate: (page: LegalPage) => void;
}

const CHAPTERS = [
  { verb: 'Festhalten.', title: 'Raus aus dem Kopf.\nRein in die Aufgabe.', text: 'Ein Gedanke, ein Foto, eine kurze Sprachnachricht. Fang da an, wo du gerade bist. Jede Aufgabe ist ein Chat – mit allem, was dazugehört.', tags: ['Text, Foto & Dateien', 'Sprache & Video'], label: 'Der erste Schritt zählt.' },
  { verb: 'Klären.', title: 'Eine kurze Frage.\nUnd es geht weiter.', text: 'Hol dein Team oder Kunden per Link dazu. Mit Rückfragen zum Antippen wird aus „Wir müssten noch klären …“ eine Antwort, mit der du arbeiten kannst.', tags: ['Gäste ohne Konto', 'Rückfragen & Formulare'], label: 'Alle wissen Bescheid.' },
  { verb: 'Anpacken.', title: 'Wer macht’s?\nBis wann? Läuft.', text: 'Gib der Aufgabe eine zuständige Person und einen Termin. Auf dem Board siehst du, wo die Arbeit steht. Der ganze Austausch bleibt direkt an der Aufgabe.', tags: ['Boards & Listen', 'Zuständigkeit & Fälligkeit'], label: 'Aus besprechen wird machen.' },
  { verb: 'Abhaken.', title: 'Dieses Gefühl?\nIst gemacht.', text: 'Haken dran. Die Aufgabe wandert ins Archiv, der Verlauf bleibt erhalten. Meilensteine halten Fortschritte fest. Und du hast wieder Platz für das Nächste.', tags: ['Archiv & Meilensteine', 'Projektübersicht'], label: 'Ein To-do weniger im Kopf.' },
];

const FEATURES: { icon: IconName; title: string; text: string }[] = [
  { icon: 'chats', title: 'Vertraut ab der ersten Nachricht.', text: 'Antworten, @Erwähnungen, Reaktionen und Weiterleiten. Zusammenarbeit fühlt sich an wie ein Gespräch.' },
  { icon: 'screen', title: 'Zeigen geht oft schneller.', text: 'Fotos, Videos, Bildschirmaufnahmen und Dateien geben deiner Aufgabe den Kontext, den Worte allein nicht liefern.' },
  { icon: 'cloudOff', title: 'Dein Gedanke wartet nicht aufs Netz.', text: 'Aufgaben auch offline erfassen. Sobald du wieder verbunden bist, werden deine Änderungen abgeglichen.' },
  { icon: 'search', title: 'Wiederfinden statt nachfragen.', text: 'Suche, Kategorien, angepinnte Aufgaben und markierte Nachrichten bringen dich zurück zum richtigen Detail.' },
  { icon: 'user', title: 'Gemeinsam. Mit klaren Zuständigkeiten.', text: 'Projekte bündeln eure Aufgaben. Zuweisungen, Fälligkeiten und Benachrichtigungen helfen, dranzubleiben.' },
  { icon: 'share', title: 'Ein Link. Alle im Gespräch.', text: 'Gib einzelne Aufgaben oder ein Projekt für Externe frei. Kunden schreiben im Browser mit, ohne eigenes Konto.' },
];


export function Landing({ onLogin, onRegister, onNavigate }: Props) {
  const [active, setActive] = useState(0);
  const [checked, setChecked] = useState<boolean[]>([false, false, false]);
  const [drawing, setDrawing] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const chapters = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const page = root.current;
    if (!page || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    page.classList.add('ms-motion-ready');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          (entry.target as HTMLElement).dataset.visible = 'true';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    page.querySelectorAll('.ms-reveal').forEach(el => observer.observe(el));
    return () => { observer.disconnect(); page.classList.remove('ms-motion-ready'); };
  }, []);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const target = window.innerHeight * 0.48;
      let closest = 0;
      let distance = Infinity;
      chapters.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const d = Math.abs(rect.top + rect.height / 2 - target);
        if (d < distance) { distance = d; closest = i; }
      });
      setActive(closest);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  const allDone = checked.every(Boolean);

  return <div ref={root} className="ms-landing" data-mode="dark">
    <header className="ms-header">
      <Logo size={32} claim />
      <nav aria-label="Hauptnavigation">
        <a href="#so-gehts">So geht’s</a><a href="#funktionen">Funktionen</a><a href="#tarife">Tarife</a>
        <button onClick={onLogin}>Anmelden</button>
        <button className="ms-button small" onClick={onRegister}>Leg los <Icon name="chev" size={16} /></button>
      </nav>
    </header>
    <main>
      <section className="ms-hero">
        <div className="ms-hero-copy">
          <div className="ms-eyebrow"><span className="ms-live-dot" /> FÜR TEAMS, DIE GEMEINSAM MEHR SCHAFFEN.</div>
          <h1>Mach’s<br /><span>einfach<span className="ms-ellipsis">...</span></span></h1>
          <p>Aufgaben klären. Verantwortung teilen. Gemeinsam umsetzen.<br />Mach’s verbindet Aufgaben und Team-Chat –<br />damit aus Absprachen Ergebnisse werden.</p>
          <div className="ms-actions"><button className="ms-button" onClick={onRegister}>Einfach loslegen <Icon name="chev" size={18} /></button><a href="#so-gehts">Entdecke Mach’s <span>↓</span></a></div>
          <small>Kostenlos starten. Ohne Kreditkarte.</small>
        </div>
        <div className="ms-robot-stage">
          <div className="ms-robot-halo" aria-hidden="true" />
          <div className="ms-robot-welcome"><LandingMako pose="welcome" celebrate={drawing} /></div>
          <div className="ms-robot-note ms-hero-message ms-message-start"><span className="ms-live-dot" /><div><small>KLEINER SCHRITT. GUTES GEFÜHL.</small><b>Einfach mal anfangen.</b></div></div>
          <button className="ms-hero-check-card ms-hero-message ms-message-done" onClick={() => setDrawing(value => value + 1)} aria-label="Hakenanimation erneut abspielen">
            <svg key={drawing} viewBox="0 0 42 42" aria-hidden="true"><path d="m9 22 8 8L34 12" pathLength="1" /></svg>
            <span><b>Der erste Schritt?</b><small>Ist gemacht.</small></span><Icon name="reset" size={13} />
          </button>
          <div className="ms-robot-note ms-hero-message ms-message-team"><Icon name="chats" size={20} /><div><small>ZUSAMMEN GEHT’S WEITER.</small><b>Eine Frage. Alle im Bild.</b></div></div>
          <div className="ms-robot-note ms-hero-message ms-message-idea"><Icon name="sparkle" size={20} /><div><small>RAUS AUS DEM KOPF.</small><b>Deine Idee hat jetzt einen Platz.</b></div></div>
        </div>
        <a className="ms-scroll" href="#so-gehts">Eine Aufgabe. Vom Anfang bis zum Haken.<span>↓</span></a>
      </section>

      <div className="ms-ribbon"><span><i>01</i>Gedanke raus.</span><b aria-hidden="true" /><span><i>02</i>Team dazu.</span><b aria-hidden="true" /><span><i className="ms-ribbon-done"><Icon name="check" size={18} /></i>Haken dran.</span></div>

      <section className="ms-story" id="so-gehts" aria-label="Eine Aufgabe vom ersten Gedanken bis zur Erledigung">
        <div className="ms-story-intro"><div><span className="ms-eyebrow">WENIGER AUFSCHIEBEN. MEHR ABHAKEN.</span><h2>Große Pläne?<br />Fang mit einer Aufgabe an.</h2><p>Zum Beispiel mit dieser kleinen Sache für die Website.</p></div><div className="ms-guide-portrait"><LandingMako pose="guide" /></div></div>
        <div className="ms-story-grid">
          <div className="ms-chapters">{CHAPTERS.map((chapter, i) => <article className={`ms-chapter ${active === i ? 'is-active' : ''}`} key={chapter.verb} ref={el => { chapters.current[i] = el; }} id={`schritt-${i + 1}`}>
            <div className="ms-step"><span>0{i + 1}</span>{chapter.verb}</div><h2>{chapter.title}</h2><p>{chapter.text}</p>
            <div className="ms-tags">{chapter.tags.map(tag => <span key={tag}>{tag}</span>)}</div>
            <div className="ms-mobile-preview" aria-hidden="true"><LandingPhone step={i} /></div>
          </article>)}</div>
          <div className="ms-sticky-wrap"><div className={`ms-sticky ms-mood-${active}`}>
            <div className="ms-phone-aura" aria-hidden="true"><span key={active} className="ms-phone-pulse" /></div>
            <div aria-hidden="true"><LandingPhone step={active} /></div>
            <div className="ms-progress" aria-label="Kapitel wählen">{CHAPTERS.map((c, i) => <a key={c.verb} href={`#schritt-${i + 1}`} aria-label={`Schritt ${i + 1}: ${c.verb}`} aria-current={i === active ? 'step' : undefined}><span className={i <= active ? 'filled' : ''} /></a>)}</div>
            <p className="ms-caption">{CHAPTERS[active].label}</p>
          </div></div>
        </div>
      </section>

      <section className="ms-features" id="funktionen">
        <div className="ms-section-heading"><div><span className="ms-eyebrow">EINFACH HEISST NICHT WENIG.</span><h2>Für alles, was<br />beim Machen hilft.</h2></div><div className="ms-feature-companion"><LandingMako pose="guide" /><p>Vom schnellen Zuruf bis zum gemeinsamen Projekt. Die Werkzeuge sind da, wenn du sie brauchst.</p></div></div>
        <div className="ms-feature-grid">{FEATURES.map(f => <article key={f.title}><Icon name={f.icon} size={25} /><h3>{f.title}</h3><p>{f.text}</p></article>)}</div>
      </section>

      <section className="ms-ai">
        <div className="ms-ai-portrait"><LandingMako pose="closeup" /><span><i className="ms-live-dot" /> Gute Frage. Nächster Schritt.</span></div>
        <div className="ms-ai-copy"><span className="ms-eyebrow"><Icon name="sparkle" size={16} /> EIN BISSCHEN RÜCKENWIND.</span><h2>Deine KI kann<br />mit anpacken.</h2><p>Lass Sprache und Videos zusammenfassen. Oder verbinde deinen KI-Assistenten über MCP, um den Projektstand abzufragen und Aufgaben anzulegen.</p>
          <div className="ms-ai-conversation"><div className="ms-ai-prompt">Was ist bei der Bäckerei noch offen?</div><div className="ms-ai-answer"><Icon name="sparkle" size={18} /><div><b>Das Sommerangebot ist erledigt.</b><p>Offen sind noch die neue Speisekarte und die Öffnungszeiten für die Feiertage.</p></div></div><small>Beispiel mit deinem verbundenen Assistenten</small></div>
          <small>KI mit eigenem Schlüssel ab Plus. MCP lesend ab Plus, Aufgaben per KI bearbeiten mit Business.</small>
        </div>
      </section>

      <LandingPlans onRegister={onRegister} />

      <section className={`ms-final ${allDone ? 'ms-all-done' : ''}`}>
        <div className="ms-final-art"><div className="ms-robot-seat" aria-hidden="true" /><LandingMako pose="done" celebrate={allDone} /><span className="ms-done-stamp"><Icon name="check" size={18} />{allDone ? 'Gut gemacht!' : 'Haken dran.'}</span></div>
        <div className="ms-final-copy"><span className="ms-eyebrow">DU MUSST NICHT ALLES AUF EINMAL MACHEN.</span><h2>Nur den<br /><span>ersten Schritt.</span></h2>
          <div className="ms-try"><p>Probier’s aus. Abhaken fühlt sich gut an.</p><div>{['Aufgabe klären', 'Im Team umsetzen', 'Ergebnis abhaken'].map((task, i) => <button key={task} className={checked[i] ? 'is-checked' : ''} aria-pressed={checked[i]} onClick={() => setChecked(previous => previous.map((value, j) => j === i ? !value : value))}><span>{checked[i] && <Icon name="check" size={14} />}</span>{task}</button>)}</div><small aria-live="polite">{allDone ? 'Siehst du? Der Anfang ist gemacht.' : `${checked.filter(Boolean).length} von 3 abgehakt. Du bist dran.`}</small></div>
          <button className="ms-button" onClick={onRegister}>Mach’s einfach... Leg los. <Icon name="chev" size={19} /></button><p>Kostenlos starten · Im Browser und mobil</p>
        </div>
      </section>
    </main>
    <LegalFooter onNavigate={onNavigate} extra={<button onClick={onLogin}>Anmelden</button>} />
  </div>;
}
